// src/components/transportations/FormAddEditTransportation.jsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Paper,
    Typography,
    Grid,
    Stack,
    Button,
    useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useCreateTransportation from "../../hooks/transportations/useCreateTransportation";
import useUpdateTransportation from "../../hooks/transportations/useUpdateTransportation";
import { useUploadMedia } from "../../hooks/media/useUploadMedia";
import FormInput from "../inputs/FormInput";
import i18next from "i18next";
import LanguageToggle from "../global/LanguageToggle";
import useSingleImageField from "../../hooks/media/useSingleImageField";

const FormAddEditTransportation = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(
        initialLang.startsWith("ar") ? "ar" : "en"
    );

    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [capacity, setCapacity] = useState("");
    const [status, setStatus] = useState("active");

    const { createTransportationMutate, isPending: isCreating } = useCreateTransportation();
    const { editTransportation, isUpdate } = useUpdateTransportation();
    const { uploadMedia, isPending: isUploading } = useUploadMedia();

    const loadingSubmit = isCreating || isUpdate;

    // ✅ Image logic extracted to hook
    const img = useSingleImageField({
        initialImage: null, // سنزامنها في edit
        uploadMedia,
        isArabic,
    });

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setCapacity(currentData?.capacity || "");
            setStatus(currentData?.status || "active");

            // ✅ تزامن صورة السيرفر + id لتفعيل الحذف
            img.setFromServerImage(currentData?.image || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();

        const payload = {
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            capacity: parseInt(capacity) || 0,
            status,

            // ✅ لا تُرسل image key لو مفيش صورة
            ...img.getImagePayload(),
        };

        if (isEdit && currentData?.id) {
            editTransportation(
                { id: currentData.id, req: payload },
                { onSuccess: () => onSubmitSuccess?.() }
            );
        } else {
            createTransportationMutate(payload, {
                onSuccess: () => onSubmitSuccess?.(),
            });
        }
    };

    const statusOptions = [
        { id: "active", name: isArabic ? "نشط" : "Active" },
        { id: "disabled", name: isArabic ? "معطل" : "Disabled" },
    ];

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box
                component="form"
                onSubmit={SubmitForm}
                sx={{
                    p: { xs: 2, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Typography variant="h6">
                        {isEdit
                            ? isArabic
                                ? "تعديل وسيلة نقل"
                                : "Edit Transportation"
                            : isArabic
                                ? "إضافة وسيلة نقل جديدة"
                                : "Create New Transportation"}
                    </Typography>

                    <LanguageToggle
                        activeLang={activeLang}
                        onChange={(lng) => setActiveLang(lng)}
                        showLabel={true}
                    />
                </Box>

                {/* Image Upload Section */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 3,
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <FormInput
                        required={false}
                        type="file"
                        name="image"
                        label={isArabic ? "صورة وسيلة النقل" : "Transportation Image"}
                        labelImage={isArabic ? "اختر الصورة" : "Choose image"}
                        labelBioImage={
                            isArabic
                                ? "اسحب وأسقط الصورة هنا أو انقر للاختيار"
                                : "Drag & drop or click to upload"
                        }
                        value={img.imagePreview}
                        onChange={img.handleImageChange}
                        isUploading={isUploading}
                        errorMessage={img.imageError}
                        successMessage={
                            img.imageName && !img.imageError
                                ? isArabic
                                    ? "تم رفع الصورة بنجاح"
                                    : "Image uploaded successfully"
                                : ""
                        }
                        inputProps={{ accept: "image/*" }}

                        // ✅ لتفعيل زر الحذف من السيرفر داخل InputFileUpload في edit
                        mediaId={img.imageId}

                        // ✅ بعد الحذف: صفّر الحالة (وبالتالي لن تُرسل image)
                        onDeleted={img.handleDeleted}
                    />
                </Box>

                <Box mb={3}>
                    <Grid container spacing={2}>
                        {showArabic && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="الاسم (عربي)"
                                    placeholder="أدخل اسم وسيلة النقل بالعربي"
                                    type="text"
                                    name="name_ar"
                                    value={nameAr}
                                    onChange={(e) => setNameAr(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        {showEnglish && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Name (English)"
                                    placeholder="Enter transportation name in English"
                                    type="text"
                                    name="name_en"
                                    value={nameEn}
                                    onChange={(e) => setNameEn(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "السعة" : "Capacity"}
                                placeholder={isArabic ? "أدخل سعة وسيلة النقل" : "Enter transportation capacity"}
                                type="number"
                                name="capacity"
                                value={capacity}
                                onChange={(e) => setCapacity(e.target.value)}
                                required
                                inputProps={{ min: 1 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "الحالة" : "Status"}
                                type="select"
                                name="status"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                options={statusOptions}
                                required
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Stack direction="row" spacing={1}>
                        <Button 
                                variant="outlined" 
                                color="inherit"
                                onClick={() => navigate(-1)}
                            >
                                {isArabic ? "تراجع" : "Cancel"}
                            </Button>
                            <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loadingSubmit || isUploading}
                        >
                            {loadingSubmit
                                ? isArabic
                                    ? "جارٍ الحفظ..."
                                    : "Saving..."
                                : isEdit
                                    ? isArabic
                                        ? "تحديث"
                                        : "Update"
                                    : isArabic
                                        ? "حفظ"
                                        : "Save"}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditTransportation;
